import { ComponentDataDestService } from '../component-data-dest.service';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { EventDTO } from '../../../dto/event/event.dto';
import { Observable, catchError, map, switchMap } from 'rxjs';
import {
  collectRelatedEventGuidsFromFormData,
  patchEventCategoryForEventGuids,
} from './event/patch-event-category-from-form';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LocationDTO } from '../../../dto/location/location.dto';
import { RoomDTO } from '../../../dto/location/room.dto';
import { EventDescriptionDTO } from '../../../dto/event/event-description.dto';
import { OrganizationDTO } from '../../../dto/organization/organization.dto';
import { buildCoursePricingFromFormData, buildCoursePriceMetadataFromFormData, resolveEventPriceFromFormData } from '../../component-data-source/data/event/event_form_data';
import {
  applyEndTimeToStartDate,
  getScheduleMode,
  getSeriesShareParticipants,
  normalizeEventFormForApi,
} from '../../component-data-source/data/event/event-schedule-form.util';
import { mapFormCateringToApi } from '../../component-data-source/data/event/event-catering-field.util';
import {
  mapAdditionalRoomsToApi,
  mapOrganizationResourceIdsToApi,
  sanitizeAdditionalRoomsFormData,
} from '../../component-data-source/data/event/event-booking-form.util';

// Helper function to format Date as local datetime string (preserves local time)
function formatLocalDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export const update_event = (
  service: ComponentDataDestService,
  componentDataSourceService: ComponentDataSourceService,
  data: any
) => {
  const categoryPatchData: Record<string, unknown> = {};
  if (data != null && typeof data === 'object' && 'event_category' in data) {
    categoryPatchData['event_category'] = (data as Record<string, unknown>)['event_category'];
  }
  const formDataBeforeNormalize = data as Record<string, unknown>;
  data = normalizeEventFormForApi(data);
  sanitizeAdditionalRoomsFormData(data);
  const eventDurationType = getScheduleMode(data);
  
  // Get router and extract event GUID from URL
  const router = service.injector.get(Router);
  const pathSegments = router.url.split('/');
  const eventGuid = pathSegments.find((segment: string) => 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
  );
  
  if (!eventGuid) {
    console.error('[update_event] No valid event GUID found in URL');
    throw new Error('No valid event GUID found in URL');
  }

  const categoryEventGuids = collectRelatedEventGuidsFromFormData(
    formDataBeforeNormalize,
    eventGuid
  );

  const cateringValue = mapFormCateringToApi(data.catering);
  
  // Create organization DTO if needed (reusing existing organization if already in data)
  const organizationDTO: OrganizationDTO = data.organization || {
    id: 0, // Will be set by backend
    org_name: '', // Will be set by backend
    data: null // Will be set by backend
  };
  
  // Create properly typed DTOs
  let predefinedLocation: LocationDTO | undefined;
  if (data.location && data.location !== 'manual') {
    predefinedLocation = {
      guid: data.location,
      locations_name: '', // Will be populated by backend
      clean_name: '', // Will be populated by backend
      data: null, // Will be populated by backend
      rooms: [],
      users: [],
      registered_percentage: 0,
      progress_percentage: 0,
      ok_percentage: 0,
      firefighter_count: 0
    };
  }
  
  let predefinedRoom: RoomDTO | undefined;
  if (data.room && data.location !== 'manual') {
    predefinedRoom = {
      guid: data.room,
      room_name: '', // Will be populated by backend
      location_guid: data.location || '', // Will be populated by backend
      capacity: 0, // Will be populated by backend
      description: '' // Will be populated by backend
    };
  }
  
  // Create properly typed description
  const description: EventDescriptionDTO = {
    event_id: 0, // Will be set by backend
    short_description: {
      da: {
        language_code: 'da',
        translation_text: data.descriptions?.find((d: any) => d.language_code === 'da')?.short_description || data.short_description || ''
      }
    },
    long_description: {
      da: {
        language_code: 'da',
        translation_text: data.descriptions?.find((d: any) => d.language_code === 'da')?.long_description || data.description || ''
      }
    }
  };
  
  let startDate = data.start_date;
  let endDate = data.end_date || data.start_date;

  if (eventDurationType === 'single_day' && data.end_time) {
    endDate = applyEndTimeToStartDate(String(data.start_date), String(data.end_time));
  }

  if (eventDurationType === 'multi_day') {
    endDate = data.end_date || data.start_date;
  }

  const additionalRooms: NonNullable<EventDTO['additional_rooms']> = mapAdditionalRoomsToApi(
    data.additional_rooms,
    { excludeRoomGuid: typeof data.room === 'string' ? data.room : null }
  );
  const organizationResources: NonNullable<EventDTO['organization_resources']> =
    mapOrganizationResourceIdsToApi(data.organization_resource_ids);
  
  // Transform and validate the data
  const eventData: Partial<EventDTO> = {
    event_type: data.event_type as 'education' | 'course' | 'exercise',
    start_date: startDate,
    end_date: endDate,
    organization: organizationDTO, // Use the organization DTO
    visibility: data.visibility as 'public' | 'private' | 'shared',
    catering: cateringValue,
    is_active: data.event_active === 'active',
    is_shown: data.is_shown === 'shown',
    wait_list_type: data.waitlist as 'room_size' | 'everyone_on_wait_list' | 'no_wait_list',
    price: resolveEventPriceFromFormData(data),
    course_pricing: buildCoursePricingFromFormData(data),
    customer_guid: data.customer_guid || null,
    ...buildCoursePriceMetadataFromFormData(data),
    destination_period: data.destination_period || 'current_period' as 'current_period' | 'last_period' | 'both_periods',
    event_duration_type: eventDurationType as EventDTO['event_duration_type'],
    series_share_participants: getSeriesShareParticipants(data),
    user_group_id: data.user_group_id != null && data.user_group_id !== ''
        ? Number(data.user_group_id)
        : (data.user_group_id === null ? null : undefined),
    
    // Use the properly typed DTOs
    predefined_location: predefinedLocation,
    predefined_room: predefinedRoom,
    
    manual_location: data.manual_location || undefined,
    manual_location_address: data.manual_location_address || undefined,
    manual_location_room_capacity: data.manual_room_capacity ? 
      Number(data.manual_room_capacity) : undefined,
    additional_rooms: additionalRooms,
    organization_resources: organizationResources,
    
    // Use the properly typed description
    description: description,
    
    // Handle items (modules and subtypes)
    items: [
      ...(data.event_modules?.event_modules || []).map((moduleId: number) => ({
        id: 0, // Will be set by backend
        event_id: 0, // Will be set by backend
        item_type: 'modules',
        item_id: moduleId,
        deleted: false
      })),
      ...(data.event_subtypes?.event_subtypes || []).map((subtypeId: number) => ({
        id: 0, // Will be set by backend
        event_id: 0, // Will be set by backend
        item_type: 'subtypes',
        item_id: subtypeId,
        deleted: false
      }))
    ]
  };

  let processedSubEvents = [];
  if ((eventDurationType === 'multiple_dates' || eventDurationType === 'multiple_dates_locations') && data.sub_events && data.sub_events.length > 0) {
      processedSubEvents = data.sub_events.map((subEvent: any, index: number) => {
          const subEventData: any = {
              sub_event_guid: subEvent.sub_event_guid,
              sub_event_date: subEvent.sub_event_date,
              sub_event_start_time: subEvent.sub_event_start_time,
              sub_event_end_time: subEvent.sub_event_end_time
          };
          
            // Include per-day location when present (series with same or varying locations)
            if (subEvent.sub_event_location) {
                subEventData.sub_event_location = subEvent.sub_event_location;
                subEventData.sub_event_room = subEvent.sub_event_room;
                subEventData.sub_event_manual_location = subEvent.sub_event_manual_location;
                subEventData.sub_event_manual_location_address = subEvent.sub_event_manual_location_address;
                subEventData.sub_event_manual_room_capacity = subEvent.sub_event_manual_room_capacity;
            }
            if (subEvent.sub_event_additional_rooms) {
                subEventData.sub_event_additional_rooms = subEvent.sub_event_additional_rooms;
            }
            if (subEvent.items && Array.isArray(subEvent.items)) {
                subEventData.items = subEvent.items;
            }
            if (subEvent.sub_event_catering) {
                subEventData.sub_event_catering = mapFormCateringToApi(
                    subEvent.sub_event_catering
                );
            }

          return subEventData;
      });
  } else {
  }

  // Prepare request body - only include sub_events if there are actual sub-events to process
  const requestBody: any = { 
      event: eventData,
      multi_day_skip_dates: data.multi_day_skip_dates || [],
  };
  
  // Only add sub_events to the request if there are actual sub-events to process
  if (processedSubEvents.length > 0) {
      requestBody.sub_events = processedSubEvents;
  } else {
  }

  const url = `${environment.apiUrl}/events/${eventGuid}`;
  

  // Return an Observable that the form will subscribe to
  // The form's isSubmitting flag prevents infinite loops from doComplete() re-triggering onCompleting
  // NOTE: We do NOT subscribe here - the form subscribes, which triggers the HTTP request
  // NOTE: We don't update the store here because the events data source requires date filters,
  // and without them, the store update would be blocked/return empty data
  return service.http.patch<EventDTO>(
    url,
    requestBody
  ).pipe(
    switchMap((response) =>
      patchEventCategoryForEventGuids(
        service.http,
        categoryEventGuids.length > 0 ? categoryEventGuids : [eventGuid],
        categoryPatchData
      ).pipe(map(() => response))
    ),
    catchError(error => {
      console.error('[update_event] Error:', error);
      console.error('[update_event] Error details:', {
        status: error.status,
        statusText: error.statusText,
        error: error.error,
        url: error.url
      });
      if (error.error?.message) {
        console.error('[update_event] Server error message:', error.error.message);
      }
      throw error;
    }),
    map(response => {
      return response;
    })
  );
}; 